import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MaterialEntryService } from '../../../services/material-entry.service';
import { MaterialEntryDraftService, MaterialEntryDraft } from '../../../services/material-entry-draft.service';
import { SupplierService } from '../../../services/supplier.service';
import { TypePaymentService } from '../../../services/typePayment.service';
import { ProductService } from '../../../services/product.service';
import { MaterialEntry } from '../../../models/material-entry.model';
import { MaterialEntryPayment } from '../../../models/material-entry-payment.model';
import { TypePayment } from '../../../models/typePayment.model';
import { CreatePurchaseDto, CreatePurchaseItemDto } from '../../../models/dtos/createPurchase.dto';
import { StepperModule } from 'primeng/stepper';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { StepPrincipalComponent } from './steps/step-principal/step-principal.component';
import { StepProdutosComponent, ProductOption } from './steps/step-produtos/step-produtos.component';
import { StepPagamentoComponent } from './steps/step-pagamento/step-pagamento.component';
import { StepTotalComponent } from './steps/step-total/step-total.component';
import { format } from 'date-fns';
import { forkJoin } from 'rxjs';
import { startWith } from 'rxjs/operators';

@Component({
  selector: 'app-form-material-entry',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    StepperModule,
    ButtonModule,
    CardModule,
    StepPrincipalComponent,
    StepProdutosComponent,
    StepPagamentoComponent,
    StepTotalComponent
  ],
  templateUrl: './form-material-entry.component.html',
  styles: [`
    :host ::ng-deep .stepper-buttons-only .p-step-header {
      pointer-events: none;
      cursor: default;
    }
  `]
})
export class FormMaterialEntryComponent implements OnInit {
  activeStep = 1;
  form!: FormGroup;
  isEdit = false;
  entryId: number | null = null;

  supplierOptions: { label: string; value: number }[] = [];
  productOptions: ProductOption[] = [];
  typePaymentOptions: { label: string; value: number }[] = [];
  typePaymentsList: TypePayment[] = [];
  saving = false;
  private productFilterTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private materialEntryService: MaterialEntryService,
    private materialEntryDraftService: MaterialEntryDraftService,
    private supplierService: SupplierService,
    private productService: ProductService,
    private typePaymentService: TypePaymentService
  ) {}

  ngOnInit(): void {
    this.buildForm();
    this.loadSuppliers();
    this.loadProducts();
    this.loadTypePayments();

    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.entryId = +id;
      this.isEdit = true;
      forkJoin({
        entry: this.materialEntryService.getById(this.entryId),
        types: this.typePaymentService.getActive()
      }).subscribe({
        next: ({ entry: entryRes, types: typesRes }) => {
          const typeList = (typesRes.data ?? []).filter((t: TypePayment) => t.active !== false);
          this.typePaymentsList = typeList;
          this.typePaymentOptions = typeList.map((t: TypePayment) => ({ label: t.description, value: t.id }));
          const e = entryRes.data;
          if (e) this.patchForm(e);
        },
        error: () => {
          if (this.entryId != null) {
            this.materialEntryService.getById(this.entryId).subscribe({
              next: (res) => { if (res.data) this.patchForm(res.data); },
              error: () => {}
            });
          }
        }
      });
    } else {
      this.addItemRow();
      this.addPaymentRow();
      const draft = this.materialEntryDraftService.getDraft();
      if (draft) {
        this.patchFormFromDraft(draft);
        this.materialEntryDraftService.clearDraft();
      }
    }
  }

  goToNewProduct(): void {
    const raw = this.form.getRawValue();
    const principal = raw?.principal ?? {};
    const emissionDate = principal.emissionDate;
    const formValueForDraft = {
      ...raw,
      principal: {
        ...principal,
        emissionDate: emissionDate instanceof Date ? emissionDate.toISOString() : (emissionDate ?? null)
      }
    };
    this.materialEntryDraftService.saveDraft({
      activeStep: this.activeStep,
      formValue: formValueForDraft,
      productOptions: [...this.productOptions]
    });
    this.router.navigate(['/app/products/new'], { queryParams: { returnTo: '/app/material-entries/new' } });
  }

  goToNewSupplier(): void {
    const raw = this.form.getRawValue();
    const principal = raw?.principal ?? {};
    const emissionDate = principal.emissionDate;
    const formValueForDraft = {
      ...raw,
      principal: {
        ...principal,
        emissionDate: emissionDate instanceof Date ? emissionDate.toISOString() : (emissionDate ?? null)
      }
    };
    this.materialEntryDraftService.saveDraft({
      activeStep: this.activeStep,
      formValue: formValueForDraft,
      productOptions: [...this.productOptions]
    });
    this.router.navigate(['/app/suppliers/new'], { queryParams: { returnTo: this.router.url } });
  }

  private patchFormFromDraft(draft: MaterialEntryDraft): void {
    this.activeStep = draft.activeStep ?? 1;
    this.productOptions = draft.productOptions?.length ? [...draft.productOptions] : [];

    const principal = this.form.get('principal') as FormGroup;
    if (draft.formValue?.principal) {
      const p = draft.formValue.principal;
      principal.patchValue({
        accessKey: p.accessKey ?? '',
        emissionDate: p.emissionDate ?? null,
        emissionTime: p.emissionTime ?? '',
        supplierId: p.supplierId ?? null
      });
    }

    const items = this.form.get('items') as FormArray;
    items.clear();
    const sourceItems = draft.formValue?.items ?? [];
    if (sourceItems.length) {
      sourceItems.forEach((it: any) => {
        items.push(this.fb.group({
          productId: [it.productId ?? null],
          productCode: [it.productCode ?? ''],
          productDescription: [it.productDescription ?? ''],
          quantity: [it.quantity ?? 1, [Validators.required, Validators.min(0.001)]],
          unitPrice: [it.unitPrice ?? 0, [Validators.required, Validators.min(0)]],
          discount: [it.discount ?? 0, [Validators.min(0)]]
        }));
      });
    } else {
      this.addItemRow();
    }

    const payments = this.form.get('payments') as FormArray;
    payments.clear();
    const sourcePayments = draft.formValue?.payments ?? [];
    if (sourcePayments.length) {
      sourcePayments.forEach((p: any) => {
        payments.push(this.fb.group({
          typePaymentId: [p.typePaymentId ?? null, Validators.required],
          value: [p.value ?? 0, [Validators.required, Validators.min(0.01)]]
        }));
      });
    } else {
      this.addPaymentRow();
    }

    const total = this.form.get('total') as FormGroup;
    if (draft.formValue?.total) {
      total.patchValue({
        totalFreight: draft.formValue.total.totalFreight ?? 0,
        totalInsurance: draft.formValue.total.totalInsurance ?? 0,
        totalOtherExpenses: draft.formValue.total.totalOtherExpenses ?? 0,
        discountOnTotal: draft.formValue.total.discountOnTotal ?? 0,
        observations: draft.formValue.total.observations ?? ''
      });
    }
  }

  private buildForm(): void {
    this.form = this.fb.group({
      principal: this.fb.group({
        accessKey: ['', [Validators.required, Validators.minLength(44), Validators.maxLength(44)]],
        emissionDate: [null, Validators.required],
        emissionTime: ['', Validators.required],
        supplierId: [null, Validators.required]
      }),
      items: this.fb.array([]),
      payments: this.fb.array([]),
      total: this.fb.group({
        totalFreight: [0],
        totalInsurance: [0],
        totalOtherExpenses: [0],
        discountOnTotal: [0],
        observations: ['']
      })
    });
    this.setupPrincipalDependencies();
  }

  private setupPrincipalDependencies(): void {
    const principal = this.principalGroup;
    const supplierIdCtrl = principal.get('supplierId');
    const accessKeyCtrl = principal.get('accessKey');
    const emissionDateCtrl = principal.get('emissionDate');
    const emissionTimeCtrl = principal.get('emissionTime');
    if (!supplierIdCtrl || !accessKeyCtrl || !emissionDateCtrl || !emissionTimeCtrl) return;
    const setNotaFiscalDisabled = (disabled: boolean) => {
      if (disabled) {
        accessKeyCtrl.disable({ emitEvent: false });
        emissionDateCtrl.disable({ emitEvent: false });
        emissionTimeCtrl.disable({ emitEvent: false });
      } else {
        accessKeyCtrl.enable({ emitEvent: false });
        emissionDateCtrl.enable({ emitEvent: false });
        emissionTimeCtrl.enable({ emitEvent: false });
      }
    };
    supplierIdCtrl.valueChanges.pipe(startWith(supplierIdCtrl.value)).subscribe((value: number | null) => {
      const hasSupplier = value != null && !isNaN(Number(value));
      setNotaFiscalDisabled(!hasSupplier);
    });
  }

  private static KEY_TO_PAYMENT_DESCRIPTION: Record<string, string> = {
    totalCash: 'dinheiro',
    totalPix: 'pix',
    totalDebitCard: 'cartão de débito',
    totalCreditCard: 'cartão de crédito',
    totalStoreCredit: 'crédito loja',
    totalDigitalWallet: 'carteira digital',
    totalBankTransfer: 'transferência bancária',
    totalDeposit: 'depósito',
    totalCheck: 'cheque',
    totalVoucher: 'vale',
    totalSlip: 'boleto',
    totalCashback: 'cashback'
  };

  private patchForm(e: MaterialEntry): void {
    const principal = this.form.get('principal') as FormGroup;
    const emissionDate = e.issueDateTime
      ? new Date(e.issueDateTime)
      : (e.emissionDate ? new Date(e.emissionDate) : null);
    const emissionTime = e.issueDateTime
      ? format(new Date(e.issueDateTime), 'HH:mm')
      : (e.emissionTime ?? '');
    principal.patchValue({
      accessKey: e.accessKey ?? '',
      emissionDate,
      emissionTime,
      supplierId: e.supplierId ?? null
    });
    const items = this.form.get('items') as FormArray;
    items.clear();
    const sourceItems = e.purchaseItems ?? e.items ?? [];
    if (sourceItems.length) {
      sourceItems.forEach((it: any) => {
        const price = it.price ?? it.unitPrice ?? 0;
        const qty = it.quantity ?? 1;
        const totalVal = it.total ?? it.totalPrice ?? qty * price;
        const discount = it.discount ?? Math.max(0, qty * price - totalVal);
        const code = it.code ?? it.productCode ?? '';
        const description = it.description ?? it.productDescription ?? '';
        items.push(this.fb.group({
          productId: [it.productId],
          productCode: [code],
          productDescription: [description],
          quantity: [qty],
          unitPrice: [price],
          discount: [discount]
        }));
        if (it.productId && !this.productOptions.some(o => o.value === it.productId)) {
          this.productOptions.push({
            label: `${code} - ${description}`.trim() || String(it.productId),
            value: it.productId,
            priceCost: price,
            code,
            description,
            measureUnityId: (it as any).measureUnityId ?? 1
          });
        }
      });
    } else {
      this.addItemRow();
    }
    const payments = this.form.get('payments') as FormArray;
    payments.clear();
    if (e.payments?.length) {
      e.payments.forEach(p => {
        payments.push(this.fb.group({
          typePaymentId: [p.typePaymentId],
          value: [p.value ?? 0]
        }));
      });
    } else {
      const totalsFromApi = [
        ['totalCash', e.totalCash],
        ['totalPix', e.totalPix],
        ['totalDebitCard', e.totalDebitCard],
        ['totalCreditCard', e.totalCreditCard],
        ['totalStoreCredit', e.totalStoreCredit],
        ['totalDigitalWallet', e.totalDigitalWallet],
        ['totalBankTransfer', e.totalBankTransfer],
        ['totalDeposit', e.totalDeposit],
        ['totalCheck', e.totalCheck],
        ['totalVoucher', e.totalVoucher],
        ['totalSlip', e.totalSlip],
        ['totalCashback', e.totalCashback]
      ] as const;
      let added = 0;
      for (const [key, value] of totalsFromApi) {
        const num = Number(value ?? 0);
        if (num <= 0) continue;
        const desc = FormMaterialEntryComponent.KEY_TO_PAYMENT_DESCRIPTION[key];
        const typePayment = this.typePaymentsList.find(
          tp => (tp.description ?? '').toLowerCase().trim() === desc
        );
        if (typePayment) {
          payments.push(this.fb.group({
            typePaymentId: [typePayment.id],
            value: [num]
          }));
          added++;
        }
      }
      if (added === 0) this.addPaymentRow();
    }
    const total = this.form.get('total') as FormGroup;
    total.patchValue({
      totalFreight: e.totalFreight ?? 0,
      totalInsurance: e.totalInsurance ?? 0,
      totalOtherExpenses: e.totalOtherExpenses ?? 0,
      discountOnTotal: e.discount ?? e.discountOnTotal ?? 0,
      observations: (e as any).observations ?? ''
    });
  }

  get principalGroup(): FormGroup {
    return this.form.get('principal') as FormGroup;
  }

  get totalGroup(): FormGroup {
    return this.form.get('total') as FormGroup;
  }

  get itemsArray(): FormArray {
    return this.form.get('items') as FormArray;
  }

  get paymentsArray(): FormArray {
    return this.form.get('payments') as FormArray;
  }

  addItemRow(): void {
    this.itemsArray.push(
      this.fb.group({
        productId: [null],
        productCode: [''],
        productDescription: [''],
        quantity: [1, [Validators.required, Validators.min(0.001)]],
        unitPrice: [0, [Validators.required, Validators.min(0)]],
        discount: [0, [Validators.min(0)]]
      })
    );
  }

  addPaymentRow(): void {
    this.paymentsArray.push(
      this.fb.group({
        typePaymentId: [null, Validators.required],
        value: [0, [Validators.required, Validators.min(0.01)]]
      })
    );
  }

  loadSuppliers(): void {
    this.supplierService.getAll().subscribe({
      next: (res) => {
        const list = res.data ?? [];
        this.supplierOptions = [
          { label: 'Selecione o fornecedor', value: null as any },
          ...list.map(s => ({ label: s.companyName, value: s.id! }))
        ];
      }
    });
  }

  loadProducts(search: string = ''): void {
    this.productService.getAll(search, true, false, false, 1).subscribe({
      next: (res) => {
        const list = (res as any).data ?? res.data ?? [];
        const products = Array.isArray(list) ? list : (list.data ?? []);
        const newOptions = products.map((p: { id: number; code: string; description: string; priceCost: number; measureUnityId?: number }) => ({
          label: `${p.code} - ${p.description}`,
          value: p.id,
          priceCost: p.priceCost ?? 0,
          code: p.code ?? '',
          description: p.description ?? '',
          measureUnityId: p.measureUnityId ?? 1
        }));
        const selectedIds = (this.itemsArray?.value ?? [])
          .map((row: { productId?: number | null }) => row.productId)
          .filter((id: number | null | undefined) => id != null && !isNaN(Number(id)));
        for (const id of selectedIds) {
          if (!newOptions.some((o: ProductOption) => o.value === id)) {
            const existing = this.productOptions.find(o => o.value === id);
            if (existing) newOptions.unshift(existing);
          }
        }
        this.productOptions = newOptions;
      },
      error: () => { this.productOptions = []; }
    });
  }

  onProductFilter(term: string): void {
    const search = (term ?? '').trim();
    if (this.productFilterTimer) clearTimeout(this.productFilterTimer);
    this.productFilterTimer = setTimeout(() => {
      this.loadProducts(search);
      this.productFilterTimer = null;
    }, 300);
  }

  loadTypePayments(): void {
    const setOptions = (list: TypePayment[]) => {
      const active = (list ?? []).filter(t => t.active !== false);
      this.typePaymentsList = active;
      this.typePaymentOptions = active.map(t => ({ label: t.description, value: t.id }));
    };
    this.typePaymentService.getActive().subscribe({
      next: (res) => {
        setOptions(res.data ?? []);
      },
      error: () => {
        this.typePaymentService.getAll().subscribe({
          next: (res) => setOptions(res.data ?? []),
          error: () => { this.typePaymentOptions = []; this.typePaymentsList = []; }
        });
      }
    });
  }

  isPrincipalValid(): boolean {
    return this.principalGroup.valid;
  }

  goNext(): void {
    if (this.activeStep === 1 && !this.isPrincipalValid()) {
      Object.keys(this.principalGroup.controls).forEach(k =>
        this.principalGroup.get(k)?.markAsTouched()
      );
      return;
    }
    if (this.activeStep < 4) {
      this.activeStep++;
    }
  }

  goPrev(): void {
    if (this.activeStep > 1) {
      this.activeStep--;
    }
  }

  goBack(): void {
    this.materialEntryDraftService.clearDraft();
    this.router.navigate(['/app/material-entries']);
  }

  submit(): void {
    this.markAllStepsTouched();
    if (this.form.invalid) return;

    const itemsPayload = this.buildPurchaseItemsPayload();
    const paymentsPayload = this.buildPaymentsPayload();

    if (itemsPayload.length === 0) {
      this.focusItemsStep();
      return;
    }
    if (paymentsPayload.length === 0) {
      this.focusPaymentsStep();
      return;
    }

    const payload = this.buildPurchasePayload(itemsPayload, paymentsPayload);
    this.saving = true;
    const req = this.isEdit && this.entryId
      ? this.materialEntryService.update(this.entryId, payload as any)
      : this.materialEntryService.create(payload);

    req.subscribe({
      next: () => {
        this.saving = false;
        this.materialEntryDraftService.clearDraft();
        this.router.navigate(['/app/material-entries']);
      },
      error: () => {
        this.saving = false;
      }
    });
  }

  private markAllStepsTouched(): void {
    [this.principalGroup, this.totalGroup].forEach(g => {
      Object.keys(g.controls).forEach(k => g.get(k)?.markAsTouched());
    });
    this.itemsArray.controls.forEach((c, i) => {
      Object.keys((c as FormGroup).controls).forEach(k =>
        this.itemsArray.at(i).get(k)?.markAsTouched()
      );
    });
    this.paymentsArray.controls.forEach((c, i) => {
      Object.keys((c as FormGroup).controls).forEach(k =>
        this.paymentsArray.at(i).get(k)?.markAsTouched()
      );
    });
  }

  private buildPurchaseItemsPayload(): CreatePurchaseItemDto[] {
    const raw = this.itemsArray.value as Array<{
      productId: number | null;
      productCode?: string;
      productDescription?: string;
      quantity: number;
      unitPrice: number;
      discount?: number;
    }>;
    return raw
      .filter(x => x.productId != null && !isNaN(Number(x.productId)) && Number(x.quantity) > 0)
      .map(x => {
        const quantity = Number(x.quantity);
        const price = Number(x.unitPrice) || 0;
        const discount = Number(x.discount) || 0;
        const totalGross = quantity * price;
        const total = Math.max(0, totalGross - discount);
        const product = this.productOptions.find(o => o.value === Number(x.productId));
        return {
          quantity,
          price,
          totalGross,
          discount,
          proportionalDiscount: discount,
          freight: 0,
          insurance: 0,
          otherExpenses: 0,
          total,
          itemNote: (x as any).itemNote?.trim() || undefined,
          productId: Number(x.productId),
          factorId: null,
          measureUnityId: product?.measureUnityId ?? 1
        } as CreatePurchaseItemDto;
      });
  }

  private buildPaymentsPayload(): MaterialEntryPayment[] {
    const raw = this.paymentsArray.value as Array<{ typePaymentId: number | null; value: number }>;
    return raw
      .filter(x => x.typePaymentId != null && !isNaN(Number(x.typePaymentId)) && Number(x.value) > 0)
      .map(x => ({
        typePaymentId: Number(x.typePaymentId),
        value: Number(x.value)
      }));
  }

  private static PAYMENT_DESCRIPTION_TO_KEY: Record<string, keyof Pick<CreatePurchaseDto, 'totalCash' | 'totalPix' | 'totalDebitCard' | 'totalCreditCard' | 'totalStoreCredit' | 'totalDigitalWallet' | 'totalBankTransfer' | 'totalDeposit' | 'totalCheck' | 'totalVoucher' | 'totalSlip' | 'totalCashback'>> = {
    'dinheiro': 'totalCash',
    'cash': 'totalCash',
    'pix': 'totalPix',
    'cartão de débito': 'totalDebitCard',
    'débito': 'totalDebitCard',
    'cartão de crédito': 'totalCreditCard',
    'crédito': 'totalCreditCard',
    'crédito loja': 'totalStoreCredit',
    'carteira digital': 'totalDigitalWallet',
    'transferência': 'totalBankTransfer',
    'transferência bancária': 'totalBankTransfer',
    'depósito': 'totalDeposit',
    'cheque': 'totalCheck',
    'vale': 'totalVoucher',
    'voucher': 'totalVoucher',
    'boleto': 'totalSlip',
    'cashback': 'totalCashback'
  };

  private buildPurchasePayload(items: CreatePurchaseItemDto[], payments: MaterialEntryPayment[]): CreatePurchaseDto {
    const p = this.principalGroup.getRawValue();
    const t = this.totalGroup.value;
    const emissionDate = p.emissionDate instanceof Date
      ? format(p.emissionDate, 'yyyy-MM-dd')
      : String(p.emissionDate || '').trim() || format(new Date(), 'yyyy-MM-dd');
    const emissionTime = String(p.emissionTime || '00:00').trim().slice(0, 5) || '00:00';
    const issueDateTime = `${emissionDate}T${emissionTime}:00`;

    const totalFreight = Number(t.totalFreight) || 0;
    const totalInsurance = Number(t.totalInsurance) || 0;
    const totalOtherExpenses = Number(t.totalOtherExpenses) || 0;
    const discountOnTotal = Number(t.discountOnTotal) || 0;
    const totalItemDiscount = items.reduce((s, i) => s + (i.discount || 0), 0);
    const totalDiscounts = totalItemDiscount + discountOnTotal;
    const totalGross = items.reduce((s, i) => s + i.totalGross, 0);
    const total = Math.max(0, totalGross - totalDiscounts + totalFreight + totalInsurance + totalOtherExpenses);

    const paymentTotals: Record<string, number> = {
      totalCash: 0, totalPix: 0, totalDebitCard: 0, totalCreditCard: 0,
      totalStoreCredit: 0, totalDigitalWallet: 0, totalBankTransfer: 0, totalDeposit: 0,
      totalCheck: 0, totalVoucher: 0, totalSlip: 0, totalCashback: 0
    };
    for (const pay of payments) {
      const typePayment = this.typePaymentsList.find(tp => tp.id === pay.typePaymentId);
      const desc = (typePayment?.description ?? '').toLowerCase().trim();
      const key = FormMaterialEntryComponent.PAYMENT_DESCRIPTION_TO_KEY[desc];
      if (key) {
        paymentTotals[key] = (paymentTotals[key] ?? 0) + Number(pay.value);
      }
    }

    return {
      items,
      issueDateTime,
      accessKey: (p.accessKey || '').trim() || '',
      supplierId: Number(p.supplierId),
      discount: discountOnTotal,
      totalFreight,
      totalInsurance,
      totalOtherExpenses,
      totalItemDiscount,
      totalDiscounts,
      totalGross,
      total,
      change: 0,
      totalCash: paymentTotals['totalCash'],
      totalPix: paymentTotals['totalPix'],
      totalDebitCard: paymentTotals['totalDebitCard'],
      totalCreditCard: paymentTotals['totalCreditCard'],
      totalStoreCredit: paymentTotals['totalStoreCredit'],
      totalDigitalWallet: paymentTotals['totalDigitalWallet'],
      totalBankTransfer: paymentTotals['totalBankTransfer'],
      totalDeposit: paymentTotals['totalDeposit'],
      totalCheck: paymentTotals['totalCheck'],
      totalVoucher: paymentTotals['totalVoucher'],
      totalSlip: paymentTotals['totalSlip'],
      totalCashback: paymentTotals['totalCashback']
    };
  }

  private focusItemsStep(): void {
    this.activeStep = 2;
  }

  private focusPaymentsStep(): void {
    this.activeStep = 3;
  }
}
